<template>
  <div class="row">
    <div class="col-12">
      <h5 data-cy="application-form-section-tenant">
        {{ $t('message.applicationForm.infoAboutTenant') }}
      </h5>
    </div>
    <div class="col-12 col-md-12 mb-2">
      <label
        for="preferredLanguage"
        class="mb-0">
        {{ $t('message.contact.preferredLanguage') }}
      </label>
      <coozzy-form-select
        id="preferredLanguage"
        v-model="preferredLanguage"
        data-cy="application-form-company-preferred-language-select">
        <option value="PREFERRED_LANGUAGE_GERMAN">
          {{ $t('message.contact.preferredLanguageValue.PREFERRED_LANGUAGE_GERMAN') }}
        </option>
        <option value="PREFERRED_LANGUAGE_ENGLISH">
          {{ $t('message.contact.preferredLanguageValue.PREFERRED_LANGUAGE_ENGLISH') }}
        </option>
        <option value="PREFERRED_LANGUAGE_ITALIAN">
          {{ $t('message.contact.preferredLanguageValue.PREFERRED_LANGUAGE_ITALIAN') }}
        </option>
        <option value="PREFERRED_LANGUAGE_FRENCH">
          {{ $t('message.contact.preferredLanguageValue.PREFERRED_LANGUAGE_FRENCH') }}
        </option>
      </coozzy-form-select>
    </div>
    <!-- has the viewing Done -->
    <div class="col-6 col-md-6">
      <label for="viewingDone">{{ $t('message.applicationForm.hasViewingDone') }} {{ (sending ? '*' : '' ) }}</label>
      <coozzy-form-select
        id="viewingDone"
        v-model="viewingDone"
        data-cy="application-form-company-viewing-done-select">
        <option value="no">
          {{ $t('message.generic.selectField.no') }}
        </option>
        <option value="yes">
          {{ $t('message.generic.selectField.yes') }}
        </option>
      </coozzy-form-select>
    </div>
    <!-- Desired start date of the contract -->
    <div class="col-12 col-md-6">
      <coozzy-form-input
        id="desiredStartOfContractCompany"
        ref="desiredStartOfContract"
        v-model="dates.desiredStartOfContract"
        data-cy="application-form-company-desired-start-input"
        :filter="'formatDate'"
        :state="$v.dates.desiredStartOfContract.$dirty && $v.dates.desiredStartOfContract.$error ? false : null"
        type="date"
        :name="$t('message.applicationForm.desiredStartOfContract') + (sending ? '*' : '' )"
        @blur="syncParsedDatesToCompany()" />
    </div>
    <div
      class="col-12 col-md-12">
      <coozzy-form-input
        v-model="name"
        data-cy="application-form-company-name-input"
        :state="$v.name.$dirty && $v.name.$error ? false : null"
        :name="$t('message.generic.companyName')+ (sending ? '*' : '' )" />
    </div>
    <!-- Address -->
    <div class="col-12 col-md-6">
      <coozzy-form-input
        v-model="company.address.street"
        data-cy="application-form-company-street-input"
        :name="$t('message.generic.street') + (sending ? '*' : '' )"
        :state="$v.street.$dirty && $v.street.$error ? false : null"
        type="text" />
    </div>
    <div class="col-12 col-md-6">
      <coozzy-form-input
        v-model="company.address.streetNumber"
        data-cy="application-form-company-house-number-input"
        :name="$t('message.generic.streetNumber') + (sending ? '*' : '' )"
        :state="$v.streetNumber.$dirty && $v.streetNumber.$error ? false : null"
        type="text" />
    </div>
    <div class="col-12 col-md-6">
      <coozzy-form-input
        v-model="company.address.zip"
        data-cy="application-form-company-zip-input"
        :name="$t('message.generic.zip') + (sending ? '*' : '' )"
        type="text"
        :prevent-letters="company.address.country === 'CH' || company.address.country === 'DE'"
        :limit="zipLength"
        :state="$v.zip.$dirty && $v.zip.$error ? false : null"
        @input="getCityFromZip"
        @keydown.tab="isCitiesSuggested ? tabClicked() : ''" />
    </div>
    <div class="col-12 col-md-6">
      <coozzy-form-input
        v-if="!isCitiesSuggested"
        v-model="company.address.city"
        data-cy="application-form-company-city-input"
        :name="$t('message.generic.city') + (sending ? '*' : '' )"
        :state="$v.city.$dirty && $v.city.$error ? false : null"
        type="text" />
      <template v-else>
        <label for="company-suggested-city">{{ $t('message.generic.city') }}*</label>
        <coozzy-form-select
          id="company-suggested-city"
          v-model="citySelect"
          data-cy="application-form-company-city-select"
          :state="$v.city.$dirty && $v.city.$error ? false : null"
          @change="cityChanged">
          <option
            v-for="(cityOption, cityIndex) in citiesValue"
            :key="cityIndex"
            :value="cityOption"
            :selected="cityIndex === 0">
            {{ cityOption.city }}
          </option>
        </coozzy-form-select>
      </template>
    </div>
    <div class="col-12 col-md-6">
      <coozzy-form-country-select
        v-model="country"
        data-cy="application-form-company-country-select"
        :initial-value="country"
        :state="$v.country.$dirty && $v.country.$error ? false : null"
        :required="true" />
    </div>
    <div class="col-12 col-md-6">
      <coozzy-form-region-select
        v-model="state"
        data-cy="application-form-company-canton-select"
        :initial-value="state"
        :state="$v.state.$dirty && $v.state.$error ? false : null"
        :country="country" />
    </div>
    <div class="col-12 col-md-6">
      <coozzy-form-input
        v-model="purpose"
        data-cy="application-form-company-business-purpose-input"
        :name="$t('message.applicationForm.businessPurpose') + (sending ? '*' : '' )"
        :state="$v.purpose.$dirty && $v.purpose.$error ? false : null" />
    </div>
    <div class="col-12 col-md-6">
      <coozzy-form-input
        v-model="website"
        data-cy="application-form-company-website-input"
        :name="$tc('message.generic.website')" />
    </div>
    <div class="col-12 col-md-6">
      <label for="tradeRegister">{{ $t('message.applicationForm.tradeRegister') }} {{ (sending ? '*' : '' ) }}</label>
      <coozzy-form-select
        id="tradeRegister"
        v-model="tradeRegisterEntry"
        data-cy="application-form-company-trade-register-select"
        :state="$v.tradeRegisterEntry.$dirty && $v.tradeRegisterEntry.$error ? false : null">
        <option value="UNDEFINED_VALUE">
          -
        </option>
        <option value="APPLIES">
          {{ $t('message.generic.selectField.yes') }}
        </option>
        <option value="DOES_NOT_APPLY">
          {{ $t('message.generic.selectField.no') }}
        </option>
      </coozzy-form-select>
    </div>
    <div class="col-12 col-md-6">
      <label for="vatLiable">{{ $t('message.applicationForm.vatLiable') }} {{ (sending ? '*' : '' ) }}</label>
      <coozzy-form-select
        id="vatLiable"
        v-model="vatLiable"
        data-cy="application-form-company-vat-liable-select"
        :state="$v.vatLiable.$dirty && $v.vatLiable.$error ? false : null">
        <option value="UNDEFINED_VALUE">
          -
        </option>
        <option value="APPLIES">
          {{ $t('message.generic.selectField.yes') }}
        </option>
        <option value="DOES_NOT_APPLY">
          {{ $t('message.generic.selectField.no') }}
        </option>
      </coozzy-form-select>
    </div>
    <div class="col-12 col-md-6">
      <coozzy-form-input
        id="founded"
        ref="founded"
        v-model="dates.foundedDate"
        data-cy="application-form-company-founded-input"
        type="date"
        :name="$t('message.applicationForm.founded')+ (sending ? '*' : '' )"
        :state="$v.dates.foundedDate.$dirty && $v.dates.foundedDate.$error ? false : null"
        @blur="syncParsedDatesToCompany()" />
    </div>
    <div class="col-12 col-md-6">
      <coozzy-form-input
        v-model="spaceUtilization"
        data-cy="application-form-company-space-utilisation-input"
        :name="$t('message.applicationForm.spaceUtilisation') + (sending ? '*' : '' )"
        :state="$v.spaceUtilization.$dirty && $v.spaceUtilization.$error ? false : null" />
    </div>
  </div>
</template>

<script lang="ts">
import Vue, { PropType } from 'vue'
import CoozzyFormInput from '@/framework/components/form/input/CoozzyFormInput.vue'
import { mapMutations, mapGetters } from 'vuex'
import CoozzyFormSelect from '@/framework/components/form/select/CoozzyFormSelect.vue'
import { validation, isZipValid } from '@/mixins/validation'
import { required } from 'vuelidate/lib/validators'
import CoozzyFormCountrySelect from '@/framework/components/form/select/CoozzyFormCountrySelect.vue'
import CoozzyFormRegionSelect from '@/framework/components/form/select/CoozzyFormRegionSelect.vue'
import { user } from '@/mixins/user'
import * as Sentry from '@sentry/vue'
import { address } from '@/mixins/address'
import { TypeDate } from '@/types/api'
import {
  ApplicationFormCompany,
  ApplicationFormCompanyBasicDataState,
  ApplicationFormIndividualBasicCityOption
} from '@/types/models'

function isUndefinedCompanySelect(value: string) {
  return value !== 'UNDEFINED_VALUE'
}

export default Vue.extend({
  name: 'ApplicationFormCompanyBasic',
  components: { CoozzyFormRegionSelect, CoozzyFormCountrySelect, CoozzyFormInput, CoozzyFormSelect },
  mixins: [user, validation, address],
  props: {
    company: {
      type: Object as PropType<ApplicationFormCompany | null>,
      default: null
    },
    sending: {
      type: Boolean,
      default: false
    }
  },
  data(): ApplicationFormCompanyBasicDataState {
    return {
      isCitiesSuggested: false,
      citiesValue: [],
      dates: {
        foundedDate: null,
        desiredStartOfContract: null
      },
      citySelect: null
    }
  },
  computed: {
    ...mapGetters('applicationForm', ['getViewingDone', 'getDesiredStartOfContract']),
    zipLength(): number {
      if (this.company) {
        const country = this.company.address.country
        if (country === 'CH') {
          return 4
        }
        if (country === 'DE') {
          return 5
        }
        return 15
      }
      return -1
    },
    name: {
      get(): string {
        return this.company?.name ?? ''
      },
      set(value: string) {
        const com = this.company
        if (!com) return
        com.name = value
      }
    },
    street: {
      get(): string {
        return this.company?.address.street ?? ''
      },
      set(value: string) {
        const com = this.company
        if (!com) return
        com.address.street = value
        this.updateCompany(com)
      }
    },
    preferredLanguage: {
      get(): string {
        return this.company?.preferredLanguage ?? ''
      },
      set(value: string) {
        const com = this.company
        if (!com) return
        com.preferredLanguage = value
        if (
          com.preferredLanguage === 'PREFERRED_LANGUAGE_UNDEFINED' ||
          com.preferredLanguage === 'PREFERRED_LANGUAGE_GERMAN'
        ) {
          this.$store.dispatch('internationalization/changeCurrentLanguage', 'de').then()
        } else if (com.preferredLanguage === 'PREFERRED_LANGUAGE_ENGLISH') {
          this.$store.dispatch('internationalization/changeCurrentLanguage', 'en').then()
        } else if (com.preferredLanguage === 'PREFERRED_LANGUAGE_FRENCH') {
          this.$store.dispatch('internationalization/changeCurrentLanguage', 'fr').then()
        } else {
          this.$store.dispatch('internationalization/changeCurrentLanguage', 'it').then()
        }
        this.updateCompany(com)
      }
    },
    streetNumber: {
      get(): string {
        return this.company?.address.streetNumber ?? ''
      },
      set(value: string) {
        const com = this.company
        if (!com) return
        com.address.streetNumber = value
        this.updateCompany(com)
      }
    },
    zip: {
      get(): string {
        return this.company?.address.zip ?? ''
      },
      set(value: string) {
        const com = this.company
        if (!com) return
        com.address.zip = value
        this.updateCompany(com)
      }
    },
    city: {
      get(): string {
        return this.company?.address.city ?? ''
      },
      set(value: string) {
        const com = this.company
        if (!com) return
        com.address.city = value
        this.updateCompany(com)
      }
    },
    country: {
      get(): string {
        return this.company?.address.country ?? ''
      },
      set(value: string) {
        const com = this.company
        if (!com) return
        com.address.country = value
        this.updateCompany(com)
      }
    },
    state: {
      get(): string {
        return this.company?.address.state ?? ''
      },
      set(value: string) {
        const com = this.company
        if (!com) return
        com.address.state = value
        this.updateCompany(com)
      }
    },
    vatLiable: {
      get(): string {
        return this.company?.vatLiable ?? 'UNDEFINED_VALUE'
      },
      set(value: string) {
        const com = this.company
        if (!com) return
        com.vatLiable = value
        this.updateCompany(com)
      }
    },
    website: {
      get(): string {
        return this.company?.website ?? ''
      },
      set(value: string) {
        const com = this.company
        if (!com) return
        com.website = value
        this.updateCompany(com)
      }
    },
    purpose: {
      get(): string {
        return this.company?.purpose ?? ''
      },
      set(value: string) {
        const com = this.company
        if (!com) return
        com.purpose = value
        this.updateCompany(com)
      }
    },
    spaceUtilization: {
      get(): string {
        return this.company?.spaceUtilization ?? ''
      },
      set(value: string) {
        const com = this.company
        if (!com) return
        com.spaceUtilization = value
        this.updateCompany(com)
      }
    },
    tradeRegisterEntry: {
      get(): string {
        return this.company?.tradeRegisterEntry ?? 'UNDEFINED_VALUE'
      },
      set(value: string) {
        const com = this.company
        if (!com) return
        com.tradeRegisterEntry = value
        this.updateCompany(com)
      }
    },
    viewingDone: {
      get(): string {
        return this.getViewingDone() === true ? 'yes' : 'no'
      },
      set(value: string) {
        if (value === 'yes') {
          this.setViewingDone(true)
        } else {
          this.setViewingDone(false)
        }
      }
    }
  },
  mounted() {
    this.setUserpreferredLanguage()
    const com = this.company
    this.dates.foundedDate = com?.founded?.year
      ? `${com.founded.year}-${com.founded.month.toString().padStart(2, '0')}-${com.founded.day
          .toString()
          .padStart(2, '0')}`
      : null
    const desiredStart = this.getDesiredStartOfContract() as TypeDate | null
    this.dates.desiredStartOfContract =
      desiredStart?.year && desiredStart.month?.toString() && desiredStart.day?.toString()
        ? `${desiredStart.year}-${desiredStart.month.toString().padStart(2, '0')}-${desiredStart.day.toString().padStart(2, '0')}`
        : null
  },
  methods: {
    ...mapMutations('applicationForm', ['updateCompany', 'setViewingDone', 'setDesiredStartOfContract']),
    setUserpreferredLanguage() {
      const userPreferredLanguage = this.currentLanguage
      if (userPreferredLanguage === 'en') {
        this.preferredLanguage = 'PREFERRED_LANGUAGE_ENGLISH'
      } else if (userPreferredLanguage === 'it') {
        this.preferredLanguage = 'PREFERRED_LANGUAGE_ITALIAN'
      } else if (userPreferredLanguage === 'fr') {
        this.preferredLanguage = 'PREFERRED_LANGUAGE_FRENCH'
      } else if (userPreferredLanguage === 'de') {
        this.preferredLanguage = 'PREFERRED_LANGUAGE_GERMAN'
      }
    },
    tabClicked() {
      if (this.citiesValue.length > 0) {
        this.citySelect = this.citiesValue[0]
        this.cityChanged(this.citiesValue[0])
      }
    },
    syncParsedDatesToCompany() {
      const com = this.company
      if (!com) return
      if (this.dates.foundedDate) {
        const date = new Date(this.dates.foundedDate)
        com.founded = {
          year: date.getFullYear(),
          month: date.getMonth() + 1,
          day: date.getDate()
        }
      }
      if (this.dates.desiredStartOfContract) {
        const date = new Date(this.dates.desiredStartOfContract)
        this.setDesiredStartOfContract({
          year: date.getFullYear(),
          month: date.getMonth() + 1,
          day: date.getDate()
        })
      }
      this.updateCompany(com)
    },
    cityChanged(event: ApplicationFormIndividualBasicCityOption) {
      const com = this.company
      if (!com) return
      com.address.city = event.city
      com.address.state = event.state
    },
    async getCityFromZip() {
      const com = this.company
      if (!com) return
      const zip = String(com.address.zip ?? '')
      if (zip.length > 3 && com.address.country === 'CH') {
        const zipData = await this.getZipInfo(zip)
        if (zipData) {
          com.address.state = zipData.state
          if (zipData.cities.length > 1) {
            this.isCitiesSuggested = true
            this.citiesValue = zipData.cities as ApplicationFormIndividualBasicCityOption[]
          } else {
            this.isCitiesSuggested = false
            com.address.city = zipData.cities[0].city
          }
        }
      }
    },
    isInvalid() {
      this.$v.$touch()
      if (this.$v.$invalid) {
        Sentry.setExtra('companyBasicValidation', this.$v)
        this.scrollToError()
        return true
      }
      return false
    }
  },
  validations() {
    return {
      name: {
        required
      },
      street: {
        required
      },
      streetNumber: {
        required
      },
      zip: {
        required,
        isZipValid: isZipValid(this.company ? this.company.address : null)
      },
      city: {
        required
      },
      country: {
        required
      },
      state: {
        required
      },
      purpose: {
        required
      },
      tradeRegisterEntry: {
        required,
        isUndefinedCompanySelect
      },
      vatLiable: {
        required,
        isUndefinedCompanySelect
      },
      spaceUtilization: {
        required
      },
      dates: {
        desiredStartOfContract: {
          required
        },
        foundedDate: {
          required
        }
      }
    }
  }
})
</script>

<style lang="scss" scoped>
.custom-radio {
  display: inline-block;
}
</style>
